import { addDays, format, getDay, isBefore, parseISO } from "date-fns";
import type { AvailabilityCalendarResponse } from "../../../../api-contracts/sites/wbe/availability-calendar";
import type { BookingAvailability } from "../../../../api-contracts/sites/wbe/booking-availability";

/**
 * Pure availability-union logic for the PackageDetailDialog calendar.
 *
 * A package binds several category ads. The category dialog (RoomInfoDialog)
 * scopes its calendar to a single category ad; a package must instead scope to
 * the UNION of all its bound categories: a date is bookable for the package
 * when ANY ONE bound category can book it. This module turns the
 * /availability-calendar feed (queried for all bound category ad ids) into the
 * `BookingAvailability` shape the shared Calendar consumes.
 *
 * IMPORTANT SCOPE: this reflects category INVENTORY availability (vacancy,
 * price, OTA arrival windows) plus the date-shaped package rules the static
 * detail carries (arrival window, stay period, arrival/stay weekdays). Other
 * package-criteria-only restrictions (blackout dates, min/max length-of-stay,
 * lead time) are NOT in either feed and are therefore NOT disabled here — those
 * remain enforced by the post-selection "not available" guard in the dialog.
 */

/** One bound category ad of a package: its calendar ad id and its category id. */
export type BoundPackageCategory = {
  categoryAdId: string;
  categoryId: string;
};

type BuildPackageAvailabilityParams = {
  calendarData: AvailabilityCalendarResponse | undefined;
  boundCategories: BoundPackageCategory[];
  /** Min vacant assets a single night must have to count as bookable. */
  requiredUnits: number;
  /** Inclusive week-aligned window the Calendar renders (incl. padding days). */
  windowFrom: Date;
  windowTo: Date;
  /** The package ad's own arrival window (YYYY-MM-DD, inclusive; null = no
   *  bound). A stay may only START within this window, intersected with each
   *  category ad's own arrival window — together they form the "availability
   *  period". Dates outside it stay usable as nights/departures. */
  packageArrivalFrom?: string | null;
  packageArrivalTo?: string | null;
  /** The package's stay period ("boendeperiod", YYYY-MM-DD inclusive; null = no
   *  bound). Unlike the arrival window, NO stayed night may fall outside it:
   *  out-of-period nights drop from `sufficientNights` below so they render
   *  fully unavailable (can't arrive, stay, or pass through). */
  stayPeriodFrom?: string | null;
  stayPeriodTo?: string | null;
  /** Weekdays (0 = Sunday .. 6 = Saturday) the package accepts as ARRIVAL day.
   *  null = every weekday. Other weekdays stay usable as nights/departures but
   *  render arrival-restricted (red, not clickable as a start date). */
  arrivalWeekdays?: number[] | null;
  /** Weekdays a stayed NIGHT may fall on. null = every weekday. Like the stay
   *  period, a night on another weekday drops out entirely. */
  stayWeekdays?: number[] | null;
  /** Carried through unchanged so stop-time logic still works. */
  stopTimes?: BookingAvailability["stopTimes"];
};

/**
 * Build the union `BookingAvailability` across a package's bound categories.
 *
 * While the calendar fetch is in flight (`calendarData` undefined) every date
 * in the window is reported unavailable — same defensive default as the single
 * category dialog — so dates can't be picked before the real data lands.
 */
export const buildPackageAvailability = ({
  calendarData,
  boundCategories,
  requiredUnits,
  windowFrom,
  windowTo,
  packageArrivalFrom = null,
  packageArrivalTo = null,
  stayPeriodFrom = null,
  stayPeriodTo = null,
  arrivalWeekdays = null,
  stayWeekdays = null,
  stopTimes = [],
}: BuildPackageAvailabilityParams): BookingAvailability => {
  const minUnits = Math.max(1, requiredUnits);
  // `!= null`, not `?.length`: an empty list means the operator unticked every
  // weekday, i.e. "no day is allowed" — same reading as the backend criteria.
  const arrivalWeekdaySet =
    arrivalWeekdays != null ? new Set(arrivalWeekdays) : null;
  const stayWeekdaySet = stayWeekdays != null ? new Set(stayWeekdays) : null;
  const weekdayOf = (key: string) => getDay(parseISO(key));

  // Vacancy summed across the bound categories, per night. The units of a
  // package need not share a category — the guest picks one per unit — so the
  // question a date has to answer is "can the package seat all the units
  // tonight", not "can any single category seat them all". Thresholding per
  // category struck out dates where the rooms existed but sat in two different
  // categories.
  const unionVacancyByNight = new Map<string, number>();
  const categories: BookingAvailability["categories"] = [];
  // Union sets across all bound categories.
  const anyBookableNight = new Set<string>();
  const anyArrivalAllowed = new Set<string>();

  for (const bound of boundCategories) {
    const adEntry = calendarData?.find((ad) => ad.id === bound.categoryAdId);
    const sufficientNights = calendarData
      ? new Set(
          (adEntry?.dates ?? [])
            // One unit's worth: whether the whole package fits is decided by
            // the summed union below.
            .filter((d) => d.effectiveAvailability >= 1)
            // Hard stay-period bound: a night outside [stayPeriodFrom,
            // stayPeriodTo] is not bookable at all, so it drops from the union
            // and renders fully unavailable (not merely arrival-restricted).
            .filter(
              (d) =>
                (!stayPeriodFrom || d.date >= stayPeriodFrom) &&
                (!stayPeriodTo || d.date <= stayPeriodTo),
            )
            .filter((d) => !stayWeekdaySet || stayWeekdaySet.has(weekdayOf(d.date)))
            .map((d) => d.date),
        )
      : new Set<string>();

    const arrivalFromKey = adEntry?.arrivalDateFrom ?? null;
    const arrivalToKey = adEntry?.arrivalDateTo ?? null;
    const arrivalAllowed = [...sufficientNights].filter(
      (key) =>
        (!arrivalFromKey || key >= arrivalFromKey) &&
        (!arrivalToKey || key <= arrivalToKey) &&
        // Intersect with the package ad's own arrival window (the availability
        // period): a stay may only start where BOTH the category and the
        // package permit arrival.
        (!packageArrivalFrom || key >= packageArrivalFrom) &&
        (!packageArrivalTo || key <= packageArrivalTo) &&
        // ...and on a weekday the package accepts arrivals.
        (!arrivalWeekdaySet || arrivalWeekdaySet.has(weekdayOf(key))),
    );

    categories.push({
      categoryId: bound.categoryId,
      // Narrowed by the union verdict below: `sufficientNights` is one unit's
      // worth, and departure validation reads THIS list (not `unavailableDates`),
      // so a night the package cannot seat has to be absent here too — otherwise
      // a 2-unit stay could be checked out across a night with one room.
      bookableNightDates: [...sufficientNights],
      arrivalAllowedDates: arrivalAllowed,
    });

    const vacancyByDate = new Map(
      (adEntry?.dates ?? []).map((d) => [d.date, d.effectiveAvailability]),
    );
    sufficientNights.forEach((d) => {
      anyBookableNight.add(d);
      unionVacancyByNight.set(
        d,
        (unionVacancyByNight.get(d) ?? 0) + (vacancyByDate.get(d) ?? 0),
      );
    });
    arrivalAllowed.forEach((d) => anyArrivalAllowed.add(d));
  }

  // Walk the visible window day-by-day (calendar-day arithmetic so DST doesn't
  // shift a date). A date is fully unavailable when NO bound category can book
  // it as a night; arrival-only-restricted when a category can book the night
  // but none permits arrival there.
  const unavailableDates: string[] = [];
  const arrivalOnlyRestrictedDates: string[] = [];
  for (let d = windowFrom; !isBefore(windowTo, d); d = addDays(d, 1)) {
    const key = format(d, "yyyy-MM-dd");
    if (!anyBookableNight.has(key) || (unionVacancyByNight.get(key) ?? 0) < minUnits) {
      unavailableDates.push(key);
      continue;
    }
    if (!anyArrivalAllowed.has(key)) {
      arrivalOnlyRestrictedDates.push(key);
    }
  }

  // Drop the nights the package as a whole cannot seat from every category's
  // list, now that the union is known.
  const unavailable = new Set(unavailableDates);
  const seatableCategories = categories.map((category) => ({
    ...category,
    bookableNightDates: category.bookableNightDates.filter(
      (date) => !unavailable.has(date),
    ),
    arrivalAllowedDates: category.arrivalAllowedDates.filter(
      (date) => !unavailable.has(date),
    ),
  }));

  return {
    unavailableDates,
    arrivalOnlyRestrictedDates,
    categories: seatableCategories,
    stopTimes,
  };
};
